import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, Megaphone, CalendarClock, TrendingUp, ShieldAlert, Search, CheckCircle } from "lucide-react";

const messageTypes = {
  message: { label: "Message", icon: Megaphone, tone: "text-[#00FF88]", badge: "bg-[#00FF88]/10 text-[#00FF88]" },
  announcement: { label: "Announcement", icon: Bell, tone: "text-[#FFCC00]", badge: "bg-[#FFCC00]/10 text-[#FFCC00]" },
  class_reminder: { label: "Class Reminder", icon: CalendarClock, tone: "text-[#00F0FF]", badge: "bg-[#00F0FF]/10 text-[#00F0FF]" },
  market_update: { label: "Market Update", icon: TrendingUp, tone: "text-[#35F5FF]", badge: "bg-[#35F5FF]/10 text-[#35F5FF]" },
  emergency: { label: "Emergency", icon: ShieldAlert, tone: "text-[#FF3B30]", badge: "bg-[#FF3B30]/10 text-[#FF3B30]" },
  general: { label: "Announcement", icon: Bell, tone: "text-[#FFCC00]", badge: "bg-[#FFCC00]/10 text-[#FFCC00]" },
};

const MessagesPage = () => {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [activeType, setActiveType] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const [announcementsRes, notificationsRes] = await Promise.all([
          axios.get(`${API}/announcements`, { withCredentials: true }),
          axios.get(`${API}/notifications`, { withCredentials: true }),
        ]);

        const broadcasts = asArray(notificationsRes.data, "notifications")
          .filter(item => {
            if (!item.type) return true;
            return ["message", "announcement", "class_reminder", "market_update", "emergency", "broadcast", "admin_message", "general"].includes(item.type);
          })
          .map(item => {
            let normalizedType = item.type || "message";
            if (["broadcast", "admin_message", "general"].includes(normalizedType)) {
              normalizedType = "message";
            }
            return {
              id: item.notification_id,
              title: item.title || "Message from Jamadhan",
              content: item.message || "",
              type: normalizedType,
              created_at: item.created_at,
              source: "broadcast",
            };
          });

        const legacyAnnouncements = asArray(announcementsRes.data, "announcements", "messages")
          .map(item => ({
            id: item.announcement_id,
            title: item.title || "Announcement",
            content: item.content || item.message || "",
            type: item.type || "announcement",
            created_at: item.created_at,
            source: "announcement",
          }));

        setItems([...broadcasts, ...legacyAnnouncements].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
      } catch (error) {
        console.error("Error fetching messages:", error); // eslint-disable-line no-console
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, []);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(item => {
      let typeMatch = false;
      if (activeType === "all") {
        typeMatch = true;
      } else if (activeType === "message") {
        typeMatch = item.type === "message" || item.type === "broadcast" || item.source === "broadcast" || !item.type;
      } else {
        typeMatch = item.type === activeType;
      }
      const textMatch = !q || `${item.title} ${item.content}`.toLowerCase().includes(q);
      return typeMatch && textMatch;
    });
  }, [items, query, activeType]);

  const filterTypes = ["all", "message", "announcement", "class_reminder", "market_update", "emergency"];

  if (loading) {
    return (
      <DashboardLayout title="Messages">
        <div className="flex h-64 items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#00FF88] border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Messages">
      <div className="space-y-5" data-testid="messages-page">
        <div className="terminal-card relative overflow-hidden rounded-lg p-5">
          <div className="absolute right-0 top-0 h-32 w-32 rounded-bl-full bg-[#00FF88]/10" />
          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#00FF88]">Updates Hub</p>
              <h2 className="mt-1 font-outfit text-2xl font-bold text-gray-900 dark:text-white">Messages & Announcements</h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-[#94A3B8]">All class reminders, course messages, market updates, and important notices in one easy place.</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] px-4 py-3">
              <p className="font-jetbrains text-2xl font-bold text-gray-900 dark:text-white">{items.length}</p>
              <p className="text-xs text-gray-500 dark:text-[#64748B]">total updates</p>
            </div>
          </div>
        </div>

        <Card className="border-gray-200 dark:border-white/[0.06] bg-white dark:bg-[#0B0D10]">
          <CardContent className="space-y-3 p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-[#64748B]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search messages..."
                className="h-11 w-full rounded-md border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] pl-10 pr-3 text-sm text-gray-900 dark:text-white outline-none transition placeholder:text-gray-400 dark:text-[#52525B] focus:border-[#00FF88]/60"
                data-testid="messages-search"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {filterTypes.map(type => {
                const label = type === "all" ? "All" : messageTypes[type]?.label || type;
                return (
                  <button
                    key={type}
                    onClick={() => setActiveType(type)}
                    className={`h-9 shrink-0 rounded-md px-3 text-xs font-bold transition-all hover:-translate-y-0.5 ${activeType === type ? "bg-[#00FF88] text-black shadow-[0_0_20px_rgba(0,255,136,.18)]" : "bg-slate-100 dark:bg-white/[0.04] text-slate-600 dark:text-[#A1A1AA] hover:bg-slate-200 dark:hover:bg-white/[0.08] hover:text-slate-900 dark:hover:text-white"}`}
                    data-testid={`message-filter-${type}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {filteredItems.length === 0 ? (
          <Card className="border-gray-200 dark:border-white/[0.06] bg-white dark:bg-[#0B0D10]">
            <CardContent className="p-12 text-center">
              <Bell className="mx-auto mb-4 h-12 w-12 text-gray-400 dark:text-[#52525B]" />
              <p className="font-outfit text-lg font-semibold text-gray-900 dark:text-white">No messages found</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-[#64748B]">New updates from Jamadhan will appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filteredItems.map(item => {
              const meta = messageTypes[item.type] || messageTypes.message;
              const Icon = meta.icon;
              return (
                <Card key={`${item.source}-${item.id}`} className="group border-gray-200 dark:border-white/[0.06] bg-white dark:bg-[#0B0D10] transition-all hover:-translate-y-0.5 hover:border-[#00FF88]/25 hover:bg-gray-50 dark:bg-white/[0.025]" data-testid={`message-card-${item.id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/[0.04]">
                        <Icon className={`h-5 w-5 ${meta.tone}`} strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-outfit text-base font-semibold text-gray-900 dark:text-white">{item.title}</h3>
                          <Badge className={`${meta.badge} border-0 text-[10px] font-jetbrains rounded-sm`}>{meta.label}</Badge>
                        </div>
                        <p className="mt-2 text-sm leading-6 text-[#CBD5E1]">{item.content}</p>
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-[#64748B]">
                          <span>{item.created_at ? new Date(item.created_at).toLocaleString("en-IN") : "Recently"}</span>
                          <span className="flex items-center gap-1 text-[#00FF88]"><CheckCircle className="h-3 w-3" /> Saved in messages</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MessagesPage;
